import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';

export const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyEmail: '',
    contactNumber: '',
    country: 'Zimbabwe',
    projectedSMS: '1-500',
    promotionCode: '',
    receiveUpdates: false,
    acceptTerms: false,
    industry: '',
    customIndustry: '',
    referralCode: '',
    password: '',
    confirmPassword: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const trimmedContact = formData.contactNumber.trim();

    // Basic validation: must be digits only, start with a non-zero digit (country prefix),
    // and be within a reasonable length (E.164-style without the leading "+").
    if (!/^[1-9]\d{6,14}$/.test(trimmedContact)) {
      setError('Please enter a valid contact number with the country prefix (digits only), e.g. 263712345678 for Zimbabwe.');
      return;
    }

    if (!formData.password || !formData.confirmPassword) {
      setError('Please enter and confirm your password.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match. Please check and try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyEmail: formData.companyEmail,
        contactNumber: trimmedContact,
        country: formData.country,
        industry: formData.industry,
        customIndustry: formData.customIndustry || undefined,
        projectedSMS: formData.projectedSMS,
        password: formData.password
      };

      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let message = 'Registration failed. Please try again.';
        try {
          const text = await res.text();
          if (text) {
            try {
              const json = JSON.parse(text);
              message = json.message || json.error || message;
            } catch {
              message = text;
            }
          }
        } catch {
          // ignore parse errors and fall back to default message
        }
        throw new Error(message);
      }

      setSuccessMessage('Registration successful! Redirecting you to the login page...');

      // Show a confirmation popup before redirecting to the login page
      window.alert('You have successfully registered on the platform. Please log in to continue.');
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Registration failed:', err);
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="block hover:opacity-90 transition-opacity">
            <img 
              src="/images/logo.jpg"
              alt="Rukuse Technology"
              className="h-14 w-auto"
            />
          </Link>
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-gray-600">Already have an account?</span>
            <Link to="/" className="text-[#FF5722] hover:underline">Log in</Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 py-12">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 gap-16">
          {/* Left Column - Benefits */}
          <div>
            {/* Banner Image */}
            <div className="mb-8">
              <img 
                src="/images/SMSregistrationpagebanner.jpg"
                alt="SMS Platform Banner"
                className="w-full h-[220px] object-cover rounded-lg"
              />
            </div>

            <h2 className="text-2xl font-bold mb-6">
              Empower Your Business Communication through SMS
            </h2>
            <h3 className="text-xl font-bold mb-8">
              Your business, your connections, our SMS platform.
            </h3>

            <div className="space-y-8">
              <div>
                <p className="text-gray-600">Our platform ensures reliability and scalability for businesses of all sizes. We've designed it to overcome local challenges, maximise communication efficiency and utilise the benefits of AI.</p>
              </div>

              <div className="flex items-start space-x-4">
                <svg className="w-6 h-6 text-[#FF5722] mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <h4 className="font-bold mb-2">Enjoy a free demo.</h4>
                  <p className="text-gray-600">Engage customers, streamline operations, and manage SMS campaigns effortlessly.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <svg className="w-6 h-6 text-[#FF5722] mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="text-gray-600">Explore our powerful APIs or simplify your process with a no-code interface—your communication, your way.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form (moved from left) */}
          <div>
            <h1 className="text-3xl font-bold mb-2">Sign up for free</h1>
            <p className="text-gray-600 mb-8">Join our SMS Management Platform today.</p>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First name
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last name
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company email
                  </label>
                  <input
                    type="email"
                    value={formData.companyEmail}
                    onChange={(e) => setFormData({...formData, companyEmail: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact number
                  </label>
                  <input
                    type="tel"
                    value={formData.contactNumber}
                    onChange={(e) => setFormData({...formData, contactNumber: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    placeholder="263"
                    required
                  />
                </div>
              </div>

              {/* Country and Projected SMS Volume */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    required
                  >
                    <option value="Zimbabwe">Zimbabwe</option>
                    {/* Add other countries */}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Projected SMSs per month
                  </label>
                  <select
                    value={formData.projectedSMS}
                    onChange={(e) => setFormData({...formData, projectedSMS: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent text-sm"
                    required
                  >
                    <option value="1-500">1 - 500 SMSs</option>
                    <option value="501-1500">501 - 1,500 SMSs</option>
                    <option value="1501-3000">1,501 - 3,000 SMSs</option>
                    <option value="3001-5000">3,001 - 5,000 SMSs</option>
                    <option value="5001-8000">5,001 - 8,000 SMSs</option>
                    <option value="8001-15000">8,001 - 15,000 SMSs</option>
                    <option value="15001-25000">15,001 - 25,000 SMSs</option>
                    <option value="25000+">25,000 SMSs and above</option>
                  </select>
                </div>
              </div>

              {/* Industry Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry
                  </label>
                  <select
                    value={formData.industry}
                    onChange={(e) => {
                      setFormData({
                        ...formData, 
                        industry: e.target.value,
                        customIndustry: e.target.value === 'other' ? formData.customIndustry : ''
                      });
                    }}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    required
                  >
                    <option value="">Select your industry</option>
                    <option value="retail">Retail & E-commerce</option>
                    <option value="healthcare">Healthcare & Medical</option>
                    <option value="finance">Banking & Financial Services</option>
                    <option value="education">Education & Training</option>
                    <option value="technology">Technology & Software</option>
                    <option value="hospitality">Hospitality & Tourism</option>
                    <option value="manufacturing">Manufacturing & Industrial</option>
                    <option value="real-estate">Real Estate & Property</option>
                    <option value="logistics">Logistics & Transportation</option>
                    <option value="media">Media & Entertainment</option>
                    <option value="non-profit">Non-Profit & NGO</option>
                    <option value="other">Other (Please specify)</option>
                  </select>
                </div>
                <div>
                  {formData.industry === 'other' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Specify your industry
                      </label>
                      <input
                        type="text"
                        value={formData.customIndustry}
                        onChange={(e) => setFormData({...formData, customIndustry: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                        placeholder="Enter your industry"
                        required
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Promotion Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Promotion code
                </label>
                <input
                  type="text"
                  value={formData.promotionCode}
                  onChange={(e) => setFormData({...formData, promotionCode: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  placeholder="Enter promotion code (optional)"
                />
              </div>

              {/* Referral Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referral code
                </label>
                <input
                  type="text"
                  value={formData.referralCode}
                  onChange={(e) => setFormData({...formData, referralCode: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  placeholder="Enter referral code (optional)"
                />
              </div>

              {/* Checkboxes */}
              <div className="space-y-4">
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.receiveUpdates}
                    onChange={(e) => setFormData({...formData, receiveUpdates: e.target.checked})}
                    className="mt-1 h-4 w-4 text-[#FF5722] border-gray-300 rounded focus:ring-[#FF5722]"
                  />
                  <span className="text-sm text-gray-600">
                    I want to receive news, trends, offers, or blogs by email.
                  </span>
                </label>

                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.acceptTerms}
                    onChange={(e) => setFormData({...formData, acceptTerms: e.target.checked})}
                    className="mt-1 h-4 w-4 text-[#FF5722] border-gray-300 rounded focus:ring-[#FF5722]"
                    required
                  />
                  <span className="text-sm text-gray-600">
                    I confirm I have read and accept the <Link to="/terms" className="text-[#FF5722] hover:underline">Terms and Conditions</Link>
                  </span>
                </label>
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full border-2 border-[#FF5722] text-[#FF5722] px-4 py-1.5 rounded-md hover:bg-[#FF5722] hover:text-white transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'CREATE ACCOUNT'}
              </button>

              <p className="text-sm text-gray-500 text-center">
                No credit card required
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}; 